import {
  Button,
  type ComboboxItem,
  Modal,
  Select,
  Stack,
  Text
} from "@mantine/core";
import { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import {
  DateRangeInput,
  type DateInputOnChangeValue
} from "../../../mantine/dates/DateRangeInput/DateRangeInput";
import { DateRangePresets } from "../../../mantine/dates/DateRangeInput/DateRangeInput.constants";
import { useToast } from "../../../mantine/notifications/useToast";
import { luxonDateTimeToBackendDateOrDateTime } from "../../../utils/dates/luxonDateTimeToBackendDateOrDateTime";

const ALL_SUPPLIERS_VALUE = "__all__";

export interface SapExportModalProps {
  isLoading?: boolean;
  isModalOpen: boolean;
  onCloseModal: () => void;
  onClickExport: (
    dateFrom: string,
    dateTo: string,
    supplierId: string | null
  ) => void;
  suppliers: Array<ComboboxItem>;
}

function SapExportModal({
  isLoading = false,
  isModalOpen,
  onCloseModal,
  onClickExport,
  suppliers
}: SapExportModalProps) {
  const { showErrorToast } = useToast();
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<DateInputOnChangeValue>([
    null,
    null
  ]);
  const [selectedSupplier, setSelectedSupplier] =
    useState<string>(ALL_SUPPLIERS_VALUE);

  const showSupplierSelect = suppliers.length > 1;

  const supplierSelectData: Array<ComboboxItem> = [
    { value: ALL_SUPPLIERS_VALUE, label: "Alle" },
    ...suppliers
  ];

  function handleClickSave() {
    const [start, end] = dateRange;
    if (!start || !end) {
      showErrorToast(t("errors.DeliveryInvoices.SapExportNoDateError"));
      return;
    }
    const dateFrom = luxonDateTimeToBackendDateOrDateTime(start, "ISO 8601");
    const dateTo = luxonDateTimeToBackendDateOrDateTime(end, "ISO 8601");
    const supplierId =
      selectedSupplier === ALL_SUPPLIERS_VALUE ? null : selectedSupplier;
    onClickExport(dateFrom, dateTo, supplierId);
  }

  function handleDateRangeChange(value: DateInputOnChangeValue) {
    setDateRange(value);
  }

  return (
    <Modal
      opened={isModalOpen}
      size="lg"
      title="Zeitraum für den Export angeben"
      onClose={onCloseModal}
    >
      <Stack gap="lg">
        <Text>
          Rechnungen exportieren, die im folgenden Zeitraum erstellt wurden:
        </Text>
        <DateRangeInput
          maxDate={DateTime.now()}
          presets={{ type: DateRangePresets.YearsAndMonths }}
          value={dateRange}
          onChange={handleDateRangeChange}
        />
        {showSupplierSelect && (
          <>
            <Text>
              (Optional) Nur Rechnungen des folgenden Lieferanten exportieren:
            </Text>
            <Select
              data={supplierSelectData}
              value={selectedSupplier}
              w={300}
              onChange={(value) =>
                setSelectedSupplier(value ?? ALL_SUPPLIERS_VALUE)
              }
            />
          </>
        )}
      </Stack>
      <ModalFooter>
        <Button variant="default" onClick={onCloseModal}>
          Abbrechen
        </Button>
        <Button loading={isLoading} onClick={handleClickSave}>
          Erstellen
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export { SapExportModal };
